import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PhoneIcon from "@mui/icons-material/Phone";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CountdownBanner from "./CountdownBanner";
import { useCart } from "../context/CartContext";
import MobileCart from "./MobileCart";
import { useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";

const navItems = [
  { labelKey: "nav.home", id: "home" },
  { labelKey: "nav.order", id: "orders", href: "/Order" },
  { labelKey: "nav.howItWorks", id: "how-it-works" },
  { labelKey: "nav.whyUs", id: "why-us" },
  { labelKey: "nav.meals", id: "meals" },
  { labelKey: "nav.contact", id: "contact" },
];

const Header = ({
  isScrolled,
  headerHeight,
  showMobileMenu,
  toggleMobileMenu,
  scrollToSection,
  showCartMenu,
  toggleCartMenu,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { cart, addToCart, removeFromCart,updateCustomMeal, updateQty } = useCart();
  const totalQuantity = cart.reduce((sum, item) => sum + item.qty, 0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNavClick = (id, href) => {
    if (isMobile) toggleMobileMenu();
    if (href) {
      navigate(href); // Go to the orders page
      scrollToTop();
    } else {
      setTimeout(() => scrollToSection(id), 300);
    }
  };

  return (
    <Box
      sx={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}
    >
      {!isScrolled && (
        <Box
          sx={{
            height: isMobile ? "2.6rem" : "2.85rem",
            backgroundColor: "#0f4d28",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            transition: "height 0.3s ease",
          }}
        >
          <CountdownBanner />
        </Box>
      )}

      <AppBar
        position="static"
        elevation={0}
        sx={{
          height: headerHeight,
          minHeight: isMobile ? "0" : "66px",
          backgroundColor: "#1b7a3d",
          justifyContent: "center",
          boxShadow: isScrolled
            ? "0 8px 24px rgba(15, 77, 40, 0.18)"
            : "0 2px 12px rgba(15, 77, 40, 0.12)",
          transition: "box-shadow 0.25s ease",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: isMobile ? 1 : 4 }}>
          {/* Left: Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && (
              <IconButton onClick={toggleMobileMenu} color="inherit">
                <MenuIcon />
              </IconButton>
            )}
          </Box>
          
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && <LanguageSwitcher />}
          </Box>
          <img
            src="/logo2.png"
            alt="Logo"
            style={{
              marginRight: isMobile ? "0" : "2rem",
              alignSelf: "center",
              paddingTop: "1.5rem",
              height: isMobile ? "140px" : "170px",
              width: isMobile ? "130px" : "150px",
            }}
          />
          {!isMobile && <Box sx={{ flexGrow: 1 }} />}
          {/* Right: Nav or Phone */}
          
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && <Box sx={{ minWidth: "20px" }}></Box>}
          </Box>
          {isMobile ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                color="inherit"
                onClick={() => (window.location.href = "tel:+972502448771")}
              >
                <PhoneIcon />
              </IconButton>
              <IconButton sx={{ color: "inherit" }} onClick={toggleCartMenu}>
                <Badge
                  badgeContent={totalQuantity}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 20,
                      height: 20,
                      padding: 0,
                      pt: 0.5,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      borderRadius: "50%",
                      top: 6,
                      right: 6,
                    },
                  }}
                >
                  <ShoppingBagOutlinedIcon />
                </Badge>
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              
              {navItems.map(({ labelKey, id, href }) => (
                <Typography
                  key={id}
                  onClick={() => handleNavClick(id, href)}
                  sx={{
                    cursor: "pointer",
                    color: "#fff",
                    fontFamily: "Tajawal",
                    fontWeight: 600,
                    fontSize: "clamp(0.75rem, 1.4vw, 0.95rem)",
                    opacity: 0.92,
                    position: "relative",
                    transition: "opacity 0.2s ease",
                    "&:hover": {
                      opacity: 1,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: -4,
                      height: 2,
                      borderRadius: 2,
                      background: "rgba(255,255,255,0.85)",
                      transform: "scaleX(0)",
                      transition: "transform 0.2s ease",
                    },
                    "&:hover::after": {
                      transform: "scaleX(1)",
                    },
                  }}
                >
                  {t(labelKey)}
                </Typography>
              ))}
              <Box sx={{ display: "flex", alignItems: "center" }}>
            {!isMobile && <LanguageSwitcher />}
          </Box>
              <IconButton sx={{ color: "inherit" }} onClick={toggleCartMenu}>
                <Badge
                  badgeContent={totalQuantity}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 20,
                      height: 20,
                      padding: 0,
                      pt: 0.5,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      borderRadius: "50%",
                      top: 6,
                      right: 6,
                    },
                  }}
                >
                  <ShoppingBagOutlinedIcon />
                </Badge>
              </IconButton>
            </Box>
            
          )}
        </Toolbar>
      </AppBar>

      <MobileCart
        cart={cart}
        updateQty={updateQty}
        showCartMenu={showCartMenu}
        toggleCartMenu={toggleCartMenu}
        removeFromCart={removeFromCart}
        updateCustomMeal={updateCustomMeal}
        isMobile={isMobile}
      />

      {/* Mobile Drawer */}
      <Drawer
        open={showMobileMenu}
        onClose={toggleMobileMenu}
        anchor={theme.direction === "rtl" ? "left" : "right"}
      >
        <Box
          sx={{
            width: "min(78vw, 320px)",
            height: "100vh",
            background:
              "linear-gradient(180deg, #f3faf5 0%, #ffffff 45%, #eaf7ef 100%)",
            display: "flex",
            flexDirection: "column",
            p: 4,
            gap: 2.5,
          }}
        >
          {navItems.map(({ labelKey, id, href }) => (
            <Typography
              key={id}
              onClick={() => handleNavClick(id, href)}
              sx={{
                cursor: "pointer",
                color: "#0f4d28",
                alignSelf: "end",
                fontFamily: "Tajawal",
                fontWeight: 700,
                fontSize: "clamp(1.15rem, 4vw, 1.6rem)",
                py: 0.5,
                "&:hover": { color: "#1b7a3d" },
              }}
            >
              {t(labelKey)}
            </Typography>
          ))}

          {/* Social Icons */}
          <Box
            sx={{
              width: "100%",
              marginTop: "auto",
              mb: "12vh",
              borderTop: "1px solid rgba(27, 122, 61, 0.2)",
              pt: 3,
              display: "flex",
              justifyContent: "center",
              gap: 3,
              alignItems: "center",
            }}
          >
            <img
              src="/instagram.png"
              alt="Instagram"
              style={{ width: "32px", height: "32px", cursor: "pointer" }}
              onClick={() =>
                window.open("https://www.instagram.com/dietchef93/", "_blank")
              }
            />
            <img
              src="/whatsapp.png"
              alt="WhatsApp"
              style={{ width: "32px", height: "32px", cursor: "pointer" }}
              onClick={() =>
                window.open("https://wa.me/+972502448771", "_blank")
              }
            />
            <img
              src="/telephone.png"
              alt="Telephone"
              style={{ width: "32px", height: "32px", cursor: "pointer" }}
              onClick={() => (window.location.href = "tel:+972502448771")}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;